import axios, { AxiosInstance } from "axios";
import { environment } from "../../env";
import {
  IPagedPosts,
  IPost,
  IPostCreate,
  ISearch,
} from "../../../components/posts/models";
import AuthService from "../auth/authService";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../../config/authConfig";
import {
  IAccount,
  IAccountSearch,
  IProfile,
  IGroup,
  IGroupMembers,
  IUpdateAccountRequest,
  IContact,
} from "../../models/accounts";
import { ApiResponse } from "./models";
import {
  IAsset,
  IAssetCreate,
  IQrCode,
} from "../../../components/admin/models";
import {
  IShapeFile,
  IShapeFileCreate,
} from "../../../components/earthtwin/models";
import { IPOI } from "../../../components/matterportViewer/matterportViewer3";
import { BlobServiceClient } from "@azure/storage-blob";
import { IPushNotification } from "../../components/utils/toasty";
import {
  ICreateLocationBookmark,
  ILocationBookmark,
} from "../../components/resiumMap/bookmarkLocations/locationBookmarkPanel";

export class API {
  msalInstance = new PublicClientApplication(msalConfig);

  acquireToken = async () => {
    var authService = AuthService();
    await this.msalInstance.initialize();
    const token = await authService.acquireToken(this.msalInstance);
    return token;
  };

  AuthInterceptor = (instance: AxiosInstance) => {
    instance.interceptors.request.use(
      async (config) => {
        const token = await this.acquireToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  };

  ErrorInterceptor = (instance: AxiosInstance) => {
    instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const apiResponse: ApiResponse<null> = {
          success: error.response?.data?.success || false,
          traceId: error.response?.data?.traceId || "",
          error:
            error.response?.data?.message ||
            error.message ||
            "An unknown error occurred",
          data: error.response?.data?.data || null,
          status: error.response?.status || 500,
        };

        console.error("API Error:", apiResponse);

        return Promise.reject(error);
      }
    );
  };

  createApiClient = (): AxiosInstance => {
    const instance = axios.create({
      baseURL: `${environment.API_URL}`,
      responseType: "json",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.AuthInterceptor(instance);
    this.ErrorInterceptor(instance);

    return instance;
  };
}

export class AccountsV1Service {
  private key = "v1/Account";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public get = () => this.apiClient.get<IAccount>(`${this.key}`);

  public getAccountUserId = (accountId: string) =>
    this.apiClient.get<IAccount>(`${this.key}/${accountId}/user_id`);

  public addContact = (accountId: string) =>
    this.apiClient.post(`${this.key}/relation/${accountId}`);

  public deleteContact = (accountId: string) =>
    this.apiClient.delete(`${this.key}/relation/${accountId}`);

  public confirmContact = (accountId: string) =>
    this.apiClient.post(`${this.key}/relation/${accountId}/accepted`);

  public searchAccounts = (query: string) =>
    this.apiClient.get<IAccountSearch[]>(`v1/accounts/search`, {
      params: {
        q: query,
      },
    });

  public updateAccount = (account: IUpdateAccountRequest) =>
    this.apiClient.post<IAccount>(this.key);

  public getEligibleContacts = () =>
    this.apiClient.get<IContact[]>(`${this.key}/eligiblecontacts`);
}

export class ProfileV1Service {
  private key = "v1/Account/Profile";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public get = (accountId: string) =>
    this.apiClient.get<IProfile>(`${this.key}/${accountId}`);
  public update = (profile: IProfile) =>
    this.apiClient.put<IProfile>(`${this.key}`, profile);
}

export class PostsV2Service {
  private key = "v2/Posts";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public get = (postId: string) =>
    this.apiClient.get<IPost>(`${this.key}/${postId}`);

  public getAll = (page?: number, pageSize?: number) =>
    this.apiClient.get<IPagedPosts>(`${this.key}`, {
      params: {
        page,
        pageSize,
      },
    });

  public getMyPosts = (page?: number, pageSize?: number) =>
    this.apiClient.get<IPagedPosts>(`${this.key}/my`, {
      params: {
        page,
        pageSize,
      },
    });

  public create = (post: IPostCreate) =>
    this.apiClient.post<IPost>(`${this.key}`, post);

  public update = (post: IPost) =>
    this.apiClient.put<IPost>(`${this.key}/${post.id}`, post);

  public delete = (postId: string) =>
    this.apiClient.delete(`${this.key}/${postId}`);

  public like = (postId: string) =>
    this.apiClient.post(`${this.key}/${postId}/like`);

  public unlike = (postId: string) =>
    this.apiClient.post(`${this.key}/${postId}/unlike`);

  public isLiked = (post: string) =>
    this.apiClient.get(`${this.key}/${post}/isliked`);

  public comment = (postId: string, message: string) =>
    this.apiClient.post(`${this.key}/${postId}/comment`, { message });

  public report = (postId: string, reason: string, description: string) =>
    this.apiClient.post(`${this.key}/${postId}/report`, {
      reason,
      description,
    });
}

export class NotificationsV1Service {
  private key = "v1/Notification";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public unreadCount = () =>
    this.apiClient.get<number>(`${this.key}/unreadcount`);

  public getAll = () =>
    this.apiClient.get<IPushNotification[]>(`${this.key}/my`);

  public update = (id: string) =>
    this.apiClient.put<IPushNotification>(`${this.key}/${id}`);
}

export class SearchV2Service {
  private key = "v2/Posts";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public main = (data: ISearch) =>
    this.apiClient.post(`${this.key}/search/advanced`, data);

  public distance = (data: ISearch) =>
    this.apiClient.post(`${this.key}/search/distance`, data);

  public advanced = (data: ISearch) =>
    this.apiClient.post(`${this.key}/search`, data);

  public imageSearch = (data: ISearch) =>
    this.apiClient.post(`${this.key}/search/image`, data);
}

export class CommentsV2Service {
  private key = "v2/Posts";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public like = (post: string, comment: string) =>
    this.apiClient.post(`${this.key}/${post}/comment/${comment}/like`);

  public unlike = (post: string, comment: string) =>
    this.apiClient.post(`${this.key}/${post}/comment/${comment}/unlike`);

  public isLiked = (post: string, comment: string) =>
    this.apiClient.get(`${this.key}/${post}/comment/${comment}/isliked`);

  public report = (post: string, comment: string) =>
    this.apiClient.post(`${this.key}/${post}/comment/${comment}/report`);
}

export class AssetsV1Service {
  private key = "v1/asset";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public create = (asset: IAssetCreate) =>
    this.apiClient.post<IAsset>(`${this.key}`, asset);

  public update = (id: string, asset: IAssetCreate) =>
    this.apiClient.put<IAsset>(`${this.key}/${id}`, asset);

  public getMyAssets = () => this.apiClient.get<IAsset[]>(`${this.key}/my`);

  public deleteAsset = (id: string) =>
    this.apiClient.delete(`${this.key}/${id}`);

  public assignPostToAsset = (postId: string, assetId: string) =>
    this.apiClient.post(`${this.key}/${assetId}/post/${postId}`);
}

export class GroupsV1Service {
  private key = "v1/group";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public createGroup = (group: {}) =>
    this.apiClient.post<IGroup>(`${this.key}`, group);

  public getGroup = () => this.apiClient.get(`${this.key}`);

  public getGroupMembers = (id: string) =>
    this.apiClient.get<IGroupMembers>(`${this.key}/${id}`);

  public deleteGroup = (id: string) =>
    this.apiClient.delete(`${this.key}/${id}`);

  public updateGroupName = (id: string, data: {}) =>
    this.apiClient.post<IGroupMembers>(`${this.key}/${id}`, data);

  public addGroupMember = (id: string, userId: string) =>
    this.apiClient.post(`${this.key}/${id}/user/${userId}`); // <IGroupMembers>

  public removeGroupMember = (id: string, userId: string) =>
    this.apiClient.delete(`${this.key}/${id}/user/${userId}`); // <IGroupMembers>
}

export class QrCodesV1Service {
  private key = "v1/qrcode";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public get = (id: string) => this.apiClient.get<IQrCode>(`${this.key}/${id}`);

  public create = (qrcode: IQrCode) =>
    this.apiClient.post<IQrCode>(`${this.key}`, qrcode);

  public update = (id: string, qrcode: IQrCode) =>
    this.apiClient.put<IQrCode>(`${this.key}/${id}`, qrcode);

  public getQrCodes = () => this.apiClient.get<IQrCode[]>("v1/qrcodes");

  public deleteQrCode = (id: string) =>
    this.apiClient.delete(`${this.key}/${id}`);
}

export class ShapeFilesV1Service {
  private key = "v1/shapefile";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public create = (shapefile: IShapeFileCreate) =>
    this.apiClient.post<IShapeFile>(`${this.key}`, shapefile);

  public get = (id: string) =>
    this.apiClient.get<IShapeFile>(`${this.key}/${id}`);

  public getShapeFiles = () =>
    this.apiClient.get<IShapeFile[]>("v1/shapefiles");

  public uploadShapeFile = async (file: File) => {
    const accountName = "foundatblobsuat";
    const containerName = "earthtwin";
    const sasToken = environment.SAS_Token as string;

    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net?${sasToken}`
    );

    const now = new Date();
    const timestamp = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
    const utcSecondsSinceEpoch = Math.round(timestamp / 1000).toString();
    const blobName = utcSecondsSinceEpoch + "-" + file.name;

    const containerClient = blobServiceClient.getContainerClient(containerName);
    await containerClient.createIfNotExists();
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    const uploadBlobResponse = await blockBlobClient.uploadData(file);

    if (uploadBlobResponse._response.status === 201) {
      const uploaded = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}`;
      return uploaded;
    }

    return null;
  };

  public deleteShapeFile = (id: string) =>
    this.apiClient.delete(`${this.key}/${id}`);

  public update = (id: string, shapefile: IShapeFileCreate) =>
    this.apiClient.put<IShapeFile>(`${this.key}/${id}`, shapefile);
}

export class POIsV1Service {
  private key = "v1/poi";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public create = (poi: IPOI) =>
    this.apiClient.post<boolean>(`${this.key}`, poi);

  public get = (id: string) => this.apiClient.get<IPOI>(`${this.key}/${id}`);

  public getPOIs = () => this.apiClient.get<IPOI[]>(`v1/pois`);

  public update = (id: string, poi: IPOI) =>
    this.apiClient.put<IPOI>(`${this.key}/${id}`, poi);

  public delete = (id: string) =>
    this.apiClient.delete<boolean>(`${this.key}/${id}`);

  public uploadFile = async (file: File) => {
    const accountName = "foundatblobsuat";
    const containerName = "matterport";
    const sasToken = environment.SAS_Token as string;

    const blobServiceClient = new BlobServiceClient(
      `https://${accountName}.blob.core.windows.net?${sasToken}`
    );

    const now = new Date();
    const timestamp = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
    const utcSecondsSinceEpoch = Math.round(timestamp / 1000).toString();
    const blobName = utcSecondsSinceEpoch + "-" + file.name;

    const containerClient = blobServiceClient.getContainerClient(containerName);
    await containerClient.createIfNotExists();
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);

    const uploadBlobResponse = await blockBlobClient.uploadData(file);

    if (uploadBlobResponse._response.status === 201) {
      const uploaded = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}`;
      return uploaded;
    }

    return null;
  };
}

export class BubbleV1Service {
  private key = "v1/bubble";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public encrypt = (user_id: string, company_id: string, role: number) =>
    this.apiClient.post(`${this.key}/encrypt`, {
      userID: `${user_id}`,
      companyID: `${company_id}`,
      role: `${role}`,
    });

  public decrypt = (token: string) =>
    this.apiClient.get<string>(`${this.key}/decrypt`, {
      params: {
        t: token,
      },
    });
}

export class LocationBookmarksV1Service {
  private key = "v1/locationbookmarks";
  private apiClient: AxiosInstance;

  constructor() {
    const api = new API();
    this.apiClient = api.createApiClient();
  }

  public create = (locationBookmark: ICreateLocationBookmark) =>
    this.apiClient.post<boolean>(`${this.key}`, locationBookmark);

  public get = (id: string) =>
    this.apiClient.get<ILocationBookmark>(`${this.key}/${id}`);

  public getMyLocationBookmarks = () =>
    this.apiClient.get<ILocationBookmark[]>(`${this.key}`);

  public update = (id: string, locationBookmark: ILocationBookmark) =>
    this.apiClient.put<ILocationBookmark>(
      `${this.key}/${id}`,
      locationBookmark
    );

  public delete = (id: string) =>
    this.apiClient.delete<boolean>(`${this.key}/${id}`);
}
